import type { MusicService, MusicServicesById } from './types';
import { amazonMusic } from './services/AmazonMusic';
import { appleMusic } from './services/AppleMusic';
import { audiomack } from './services/Audiomack';
import { audius } from './services/Audius';
import { boomplay } from './services/Boomplay';
import { deezer } from './services/Deezer';
import { demoify } from './services/Demoify';
import { fileBackup } from './services/FileBackup';
import { gaana } from './services/Gaana';
import { jiosaavn } from './services/JioSaavn';
import { napster } from './services/Napster';
import { pandora } from './services/Pandora';
import { qobuz } from './services/Qobuz';
import { qqMusic } from './services/QQMusic';
import { resso } from './services/Resso';
import { sampleLibrary } from './services/SampleLibrary';
import { soundcloud } from './services/SoundCloud';
import { spotify } from './services/Spotify';
import { tidal } from './services/Tidal';
import { vkMusic } from './services/VKMusic';
import { yandexMusic } from './services/YandexMusic';
import { youtube } from './services/YouTube';
import { youtubeMusic } from './services/YouTubeMusic';
import { zvuk } from './services/Zvuk';

/**
 * Canonical list of all music services FYM supports. Add new services here
 * after adding their definition under `./services/`. The aggregator is the
 * public surface for "give me every service"; individual services should
 * be imported via their subpath (e.g. `fym-shared/src/music-services/services/Spotify`)
 * for tree-shaking.
 */
export const allMusicServices: MusicService[] = [
  amazonMusic,
  appleMusic,
  deezer,
  pandora,
  soundcloud,
  spotify,
  tidal,
  youtube,
  youtubeMusic,
  napster,
  qobuz,
  qqMusic,
  yandexMusic,
  vkMusic,
  zvuk,
  gaana,
  jiosaavn,
  resso,
  boomplay,
  audiomack,
  fileBackup,
  audius,
  // The demo's two ends. Both carry `isSupported: false`, so they are in the
  // index — which is what lets the api name them — and in no picker.
  demoify,
  sampleLibrary,
];

/**
 * An empty lookup table that inherits nothing.
 *
 * These maps are indexed with strings that arrive from the outside — a request
 * body, a stored integration row, a deep link. A plain `{}` answers
 * `map['constructor']` with `Object`'s constructor: truthy, so an
 * `if (!service)` guard passes it through, and the caller then reads
 * `service.musicAPIID` off a function and gets `undefined`. On the API that
 * turned one hostile string in a 500-entry batch into a 500 for the whole
 * batch, on an unauthenticated route. With no prototype there is nothing to
 * inherit and an unknown name is simply unknown.
 */
const emptyIndex = (): MusicServicesById => Object.create(null) as MusicServicesById;

const buildIndex = <K extends string | number | symbol>(
  getKey: (service: MusicService) => K | undefined
): MusicServicesById => {
  const map = emptyIndex();
  for (const service of allMusicServices) {
    const key = getKey(service);
    if (key !== undefined) {
      map[String(key)] = service;
    }
  }
  return map;
};

export const musicServicesById: MusicServicesById = buildIndex((s) => s.id);
export const musicServicesByImporterID: MusicServicesById = buildIndex((s) => s.importerID);
export const musicServicesByMusicAPIID: MusicServicesById = buildIndex((s) => s.musicAPIID);
export const musicServicesByLinkType: MusicServicesById = buildIndex((s) => s.linkType);

/**
 * Multi-key lookup: indexed by `importerID`, `id`, `appId`, AND `musicAPIID`
 * — same write order as the legacy `fym-common` aggregator so any future
 * cross-key collision resolves identically (last-write-wins). The `appId`
 * pass is the one addition over legacy; today no service has an `appId`
 * that diverges from `id` / `importerID`, so it's a forward-compat bonus
 * key that never collides. Prefer the targeted `musicServicesBy<X>`
 * indexes in new code.
 */
export const musicServicesByAppID: MusicServicesById = (() => {
  const map = emptyIndex();
  for (const service of allMusicServices) {
    map[service.importerID] = service;
    map[service.id] = service;
    if (service.appId !== undefined) {
      map[service.appId] = service;
    }
    if (service.musicAPIID !== undefined) {
      map[service.musicAPIID] = service;
    }
  }
  return map;
})();
